import { useState, useEffect, useMemo } from "react";
import { getWorkerTemplateConfigForCountry } from "@/config/workerCountryTemplateMap";
import { getServerTemplateConfig, type ServerTemplateConfig } from "@/lib/serverTemplateDetection";
import { isBaseTemplateFamily } from "@/lib/workerTemplateNames";
import { sanitizePrivacyForClient } from "@/lib/geoSim/privacyGate";

/** Coherente con Index.tsx / SEO (US, España, resto → LATAM incl. UNKNOWN). */
export type WorkerSeoRegion = "US" | "SPAIN" | "LATAM";

function seoRegionFromCountry(country: string | undefined | null): WorkerSeoRegion {
  if (country === "US") return "US";
  if (country === "ES") return "SPAIN";
  return "LATAM";
}

/**
 * Hook to determine which template to use based on server configuration
 * All configuration comes from Cloudflare Worker injection
 */
export const useTemplateSelection = () => {
  // Initialize with server config
  const [config, setConfig] = useState<ServerTemplateConfig>(() => {
    const serverConfig = getServerTemplateConfig();
    
    if (serverConfig) {
      return serverConfig;
    }
    
    // Fallback if no server config (shouldn't happen in production)
    return {
      country: 'UNKNOWN',
      templateName: 'BaseTemplate',
      bioLayout: null,
      language: 'en-US',
      province: null,
      cfCountry: 'UNKNOWN',
      timezone: '',
      city: '',
      detectionMethod: 'fallback'
    };
  });

  // Privacy gate SPA layer: super admin (session flag from toolbar/Clerk) or
  // geo_sim+Clean. No Clerk hooks here — useTemplateSelection runs on Clerk-deferred
  // landings (DualCTA home). See src/lib/geoSim/privacyGate.ts.
  const privacySanitized = useMemo(
    () =>
      sanitizePrivacyForClient({
        adminGeoSim: config.adminGeoSim,
        privacyFlags: config.privacyFlags,
        vpnDetected: config.vpnDetected === true,
        isHosting: config.isHosting === true,
      }),
    [config.adminGeoSim, config.privacyFlags, config.vpnDetected, config.isHosting],
  );
  
  // Check query params for manual country override (QA / dev).
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    const countryOverride = params.get("country");
    if (!countryOverride || countryOverride.length !== 2) return;

    const cc = countryOverride.toUpperCase();
    // test_country alimenta resolveCheckoutCountryCode (gateway/precio del checkout).
    localStorage.setItem("test_country", cc);

    // ── Geo-sim / debug authorized session ──────────────────────────────────
    // NUNCA strippear `country` ni forzar reload que pierda el override del edge.
    // `geo_sim=1` es el transporte del toolbar; `adminGeoSim` en config = cookie
    // HMAC ya validada por el Worker; sessionStorage = simulación activa en tab.
    const hasGeoSimFlag = params.get("geo_sim") === "1";
    const hasDebugParam = params.has("debug");
    let hasGeoSimSession = false;
    try {
      hasGeoSimSession = !!sessionStorage.getItem("admin_geo_sim_session");
    } catch {
      /* ignore */
    }
    const injected = getServerTemplateConfig();
    const hasEdgeGeoSim =
      !!(
        window as unknown as {
          __TEMPLATE_CONFIG__?: { adminGeoSim?: unknown };
        }
      ).__TEMPLATE_CONFIG__?.adminGeoSim;

    if (hasGeoSimFlag || hasDebugParam || hasGeoSimSession || hasEdgeGeoSim) {
      // Faithful: confiar en el Worker si country coincide; no tocar URL.
      if (injected && injected.country === cc) {
        return;
      }
      // Mismatch o wait — no strippear country (banner del toolbar reporta).
      return;
    }

    // ── Caso FAITHFUL ────────────────────────────────────────────────────────
    // El Worker YA sirvió este país desde el init (p. ej.
    // `?debug=$WORKER_DEBUG_SECRET&country=XX`, o realmente estás en XX). Su config
    // inyectada ya trae template + dualCta + gateway correctos desde la primera
    // pintada → confiar en el Worker: NO pisar con el mirror ni recargar (recargar
    // perdería el override del Worker). Esta es la forma REAL de testear un país.
    if (injected && injected.country === cc) {
      return;
    }

    // ── Caso best-effort (dev local sin Worker, o país real ≠ XX y SIN debug
    // secret) ────────────────────────────────────────────────────────────────
    // El Worker no puede servir XX sin el debug secret, así que recargar NO ayuda
    // (antes recargaba y volvía al país real → el override nunca pegaba). Hacemos
    // un override client-side por el mirror (solo cambia templateName; el dualCta
    // real es server-data y no está disponible acá) sin recargar. Para un test
    // FIEL en prod usar `?debug=$WORKER_DEBUG_SECRET&country=XX`.
    const overrideConfig = getWorkerTemplateConfigForCountry(cc);
    setConfig(overrideConfig);
    params.delete("country");
    const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ""}`;
    window.history.replaceState({}, "", newUrl);
  }, []);
  
  const shouldUseBaseTemplate = () => isBaseTemplateFamily(config.templateName);
  const shouldUseBioLinkTemplate = () => config.templateName === "BioLinkTemplate";
  const shouldUseArgentinaBioTemplate = () => config.templateName === "ArgentinaBioTemplate";
  const shouldUseBioTemplatePremium = () => config.templateName === "BioTemplatePremium";

  const region = seoRegionFromCountry(config.country);

  return {
    // Core template data
    templateName: config.templateName,
    shouldUseBaseTemplate,
    shouldUseBioLinkTemplate,
    shouldUseArgentinaBioTemplate,
    shouldUseBioTemplatePremium,
    
    // Location data
    country: config.country,
    region,
    province: config.province,
    city: config.city || "",
    language: config.language,
    
    // Template variants
    isSpainBio: config.templateName === "BioLinkTemplate" && config.bioLayout === "spain_bio_link",
    isUSBio: config.templateName === "BioLinkTemplate" && config.bioLayout === "us_bio_link",
    isBaseTemplate: isBaseTemplateFamily(config.templateName),
    isArgentinaBio: config.templateName === "ArgentinaBioTemplate",
    isPremiumInvite: config.templateName === "BioTemplatePremium",
    
    // Location flags
    isArgentina: config.country === "AR",
    isBuenosAires: config.province === "Buenos Aires" || config.province === "CABA",

    // Best Friend Instagram (Close Friends) button (Argentina provinces: CABA, Buenos Aires, Santa Fe, etc.)
    bestfriendInstagram: !!config.whatsappBroadcast,
    hideCta: !!config.hideCta,
    showCommunity: !!config.showCommunity,
    showBirthday: !!config.showBirthday,
    showWishlist: !!config.showWishlist,
    showCheckout: !!config.showCheckout,
    showBestFriends: !!config.showBestFriends,
    // Admin heroCTA override (Worker gatea por BaseTemplate family). Sin override → undefined,
    // BaseTemplate cae al geo CTA derivado por flags arriba.
    heroCta: config.heroCta,
    // VPN/privacy: sanitized when super admin or geo_sim+Clean (SPA bypass).
    // VPN sim (anonymous) keeps real lock signals for QA.
    vpnDetected: privacySanitized.vpnDetected,
    asOrganization: privacySanitized.privacyBypassed
      ? null
      : config.asOrganization || null,
    // ── IPLocate enrichment fields (added 2026-06) ────────────────────
    neighborhood: config.neighborhood || null,
    neighborhoodCity: config.neighborhoodCity || null,
    geoSource: config.geoSource || "cf_only",
    privacyFlags: privacySanitized.privacyFlags,
    isHosting: privacySanitized.isHosting,
    privacyLockActive: privacySanitized.privacyLockActive,
    privacyBypassed: privacySanitized.privacyBypassed,
    adminGeoSim: config.adminGeoSim || null,
    asnDetails: config.asnDetails || null,
    isSpainBioCountry: config.bioLayout === "spain_bio_link",
    
    // Bio layout info
    bioLayout: config.bioLayout,
    
    // Technical info
    timezone: config.timezone || (typeof window !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : null),
    browserLang: typeof window !== "undefined" ? navigator.language : null,
    
    // Status - always ready with server config
    isEnhancedLoaded: true,
    detectionMethod: config.detectionMethod,
    isLoading: false,
    loadingStage: 'complete' as const,
    loadingText: '',
    isReady: true,
    
    // Loading protection - not needed
    isProtected: false,
    isInCooldown: false,
    canChangeTemplate: true,
    requestTemplateChange: () => {},
    loadingProgress: 100
  };
};
